import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface CreateArtifactModalProps {
  onClose: () => void;
  onSubmit: (artifactData: {
    artifactType: string;
    name: string;
    description?: string;
    isRequired?: boolean;
    data?: any;
  }) => void;
}

const ARTIFACT_TYPES = [
  { value: 'document', label: 'Dokument', description: 'Spezifikationen, Anforderungen, Reports' },
  { value: 'template', label: 'Vorlage', description: 'Wiederverwendbare Templates und Muster' },
  { value: 'resource', label: 'Ressource', description: 'Links, Tools, externe Materialien' },
  { value: 'deliverable', label: 'Lieferobjekt', description: 'Konkrete Projektergebnisse' },
  { value: 'checklist', label: 'Checkliste', description: 'Prüflisten und Qualitätssicherung' }
];

export const CreateArtifactModal: React.FC<CreateArtifactModalProps> = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    artifactType: 'document',
    name: '',
    description: '',
    isRequired: false,
    data: {}
  });

  const [jsonData, setJsonData] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const validateJson = (jsonString: string) => {
    if (!jsonString.trim()) {
      setJsonData('{}');
      setFormData(prev => ({ ...prev, data: {} }));
      setJsonError(null);
      return;
    }

    try {
      const parsed = JSON.parse(jsonString);
      setFormData(prev => ({ ...prev, data: parsed }));
      setJsonError(null);
    } catch (error) {
      setJsonError('Ungültiges JSON-Format');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    if (jsonError) {
      return;
    }

    onSubmit({
      artifactType: formData.artifactType,
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      isRequired: formData.isRequired,
      data: formData.data
    });
  };

  const selectedType = ARTIFACT_TYPES.find(type => type.value === formData.artifactType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Neues Artefakt erstellen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Artifact Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Artefakt-Typ
            </label>
            <div className="grid grid-cols-1 gap-3">
              {ARTIFACT_TYPES.map((type) => (
                <label key={type.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="artifactType"
                    value={type.value}
                    checked={formData.artifactType === type.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, artifactType: e.target.value }))}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{type.label}</div>
                    <div className="text-sm text-gray-500">{type.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Name für ${selectedType?.label}...`}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Beschreibung
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Beschreibung des Artefakts..."
            />
          </div>

          {/* Required Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isRequired"
              checked={formData.isRequired}
              onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isRequired" className="ml-2 text-sm text-gray-900">
              Dieses Artefakt ist erforderlich für den Projektabschluss
            </label>
          </div>

          {/* JSON Data */}
          <div>
            <label htmlFor="jsonData" className="block text-sm font-medium text-gray-700 mb-2">
              Zusätzliche Daten (JSON)
            </label>
            <textarea
              id="jsonData"
              value={jsonData}
              onChange={(e) => {
                setJsonData(e.target.value);
                validateJson(e.target.value);
              }}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                jsonError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder='{"file_path": "/path/to/file", "priority": "high"}'
            />
            {jsonError && (
              <p className="mt-1 text-sm text-red-600">{jsonError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Optionale strukturierte Daten für das Artefakt (z.B. Dateipfade, Prioritäten, etc.)
            </p>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!formData.name.trim() || !!jsonError}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Artefakt erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};